'use client';

import { c as _c } from "react/compiler-runtime";
import './nav-card.scss';
import { useTranslation } from 'react-i18next';
import { useRandomChild } from '@sola/ark-core/contexts/random-child.tsx';
import { cn } from '@sola/ark-core/utils/helpers.ts';
import { countryToIso } from '@sola/schema/dotcom/shared/geo.ts';
import { FlexibleContentProps, SacFilterProps } from '@sola/schema/lib/schema.ts';
import { knownSkippyUrls } from '@sola/skippy-sdk/urls.ts';
import { Image } from '../../atoms/image/image.tsx';
import { ChildBioSummary } from '../child-bio-details/child-bio-summary.tsx';
import { FeatureBase } from '../feature-base/feature-base.tsx';
import { NavCardSkeleton } from './nav-card-skeleton.tsx';
type NavCardProps = FlexibleContentProps & {
  className?: string;
};
type NavCardSacProps = SacFilterProps & {
  className?: string;
};
const NavCardContent: React.FC<NavCardProps> = t0 => {
  const $ = _c(15);
  let className;
  let media;
  let props;
  if ($[0] !== t0) {
    const {
      media: t1,
      subtitle: _subtitle,
      icon: _icon,
      quotation: _quotation,
      className: t2,
      ...t3
    } = t0;
    media = t1;
    className = t2;
    props = t3;
    $[0] = t0;
    $[1] = className;
    $[2] = media;
    $[3] = props;
  } else {
    className = $[1];
    media = $[2];
    props = $[3];
  }
  let t1;
  if ($[4] !== className) {
    t1 = cn("NavCard w-full max-w-sm mx-auto flex flex-cols items-center", className);
    $[4] = className;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  let t2;
  if ($[6] !== media?.image) {
    t2 = media?.image && <div className="NavCard__media"><Image image={media?.image} hasPlaceholder={true} aspectRatio="square" objectPosition="top" rounded="md" /></div>;
    $[6] = media?.image;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  let t3;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = cn("NavCard__body");
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  let t4;
  if ($[9] !== props) {
    t4 = <div className="NavCard__content"><FeatureBase {...props} titleSize="lg" titleVariant="label" bodySize="md" bodyVariant="label" buttonSize="md" primaryCTAVariant="tertiary" primaryCTAIconName="arrow-right" primaryCTAIconPlacement="after" className={t3} variantSpacing="card" alignment="left" inline={true} buttonsAsLinks={true} /></div>;
    $[9] = props;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  let t5;
  if ($[11] !== t1 || $[12] !== t2 || $[13] !== t4) {
    t5 = <div className={t1}>{t2}{t4}</div>;
    $[11] = t1;
    $[12] = t2;
    $[13] = t4;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  return t5;
};
const NavCardSac = props => {
  const $ = _c(16);
  const {
    className
  } = props;
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = ["bio", "geo"];
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const {
    t
  } = useTranslation(t0);
  const {
    data: childData,
    isLoading
  } = useRandomChild();
  const t1 = childData?.country;
  const t2 = childData?.localId;
  let t3;
  if ($[1] !== t1 || $[2] !== t2) {
    t3 = countryToIso(t1, t2);
    $[1] = t1;
    $[2] = t2;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const countryIso = t3;
  const countryKey = `geo:countries.${countryIso}.nameS`;
  let t4;
  if ($[4] !== countryKey || $[5] !== t) {
    t4 = t(countryKey);
    $[4] = countryKey;
    $[5] = t;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const translatedCountry = t4;
  let t5;
  if ($[7] !== childData || $[8] !== props || $[9] !== t || $[10] !== translatedCountry) {
    t5 = childData ? {
      title: childData.name.preferred,
      body: <ChildBioSummary className="NavCard__body" short={true} details={childData} translatedCountry={translatedCountry} />,
      media: {
        image: {
          url: childData.imageUrl,
          height: 124,
          width: 124,
          ratio: "square",
          alt: t("bio:picAlt", {
            childName: childData.name.preferred
          })
        }
      },
      primaryCTA: {
        title: t("bio:title", {
          childName: childData.name.preferred
        }),
        link: `${knownSkippyUrls.childBio}?gid=${childData.globalId}`
      }
    } : props;
    $[7] = childData;
    $[8] = props;
    $[9] = t;
    $[10] = translatedCountry;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  const cardProps = t5;
  let t6;
  if ($[12] !== cardProps || $[13] !== className || $[14] !== isLoading) {
    t6 = isLoading ? <NavCardSkeleton className={className} /> : <NavCardContent {...cardProps} className={className} />;
    $[12] = cardProps;
    $[13] = className;
    $[14] = isLoading;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  return t6;
};
export const NavCard = t0 => {
  const $ = _c(6);
  let props;
  if ($[0] !== t0) {
    ({
      ...props
    } = t0);
    $[0] = t0;
    $[1] = props;
  } else {
    props = $[1];
  }
  if ("limit" in props) {
    let t1;
    if ($[2] !== props) {
      t1 = <NavCardSac {...props} />;
      $[2] = props;
      $[3] = t1;
    } else {
      t1 = $[3];
    }
    return t1;
  }
  let t1;
  if ($[4] !== props) {
    t1 = <NavCardContent {...props} />;
    $[4] = props;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  return t1;
};